import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Slider,
  IconButton,
  Tabs,
  Tab,
  Alert
} from '@mui/material';
import {
  Phone,
  WhatsApp,
  Email,
  Sms,
  PersonPin,
  Close,
  Add,
  TrendingUp,
  History
} from '@mui/icons-material';
import { apiService } from '../../services/api';

interface ContactLogDialogProps {
  open: boolean;
  onClose: () => void;
  renewalId: string;
  clientName: string;
  clientEmail?: string;
  deviceImei: string;
}

interface ContactLog {
  _id: string;
  contactMethod: string;
  contactStatus: string;
  clientBehavior: string;
  conversationNotes: string;
  renewalProbability: number;
  contactDate: string;
  followUpDate?: string;
  contactedBy: {
    firstName: string;
    lastName: string;
  };
}

const TabPanel = ({ children, value, index }: any) => (
  <div hidden={value !== index} style={{ paddingTop: 16 }}>
    {value === index && children}
  </div>
);

const ContactLogDialog: React.FC<ContactLogDialogProps> = ({
  open,
  onClose,
  renewalId,
  clientName,
  clientEmail,
  deviceImei
}) => {
  const [currentTab, setCurrentTab] = useState(0);
  const [contactLogs, setContactLogs] = useState<ContactLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    contactMethod: '',
    contactStatus: '',
    clientBehavior: '',
    conversationNotes: '',
    renewalProbability: 50,
    followUpDate: ''
  });

  const contactMethods = [
    { value: 'phone', label: 'Teléfono', icon: <Phone fontSize="small" /> },
    { value: 'whatsapp', label: 'WhatsApp', icon: <WhatsApp fontSize="small" /> },
    { value: 'email', label: 'Email', icon: <Email fontSize="small" /> },
    { value: 'sms', label: 'SMS', icon: <Sms fontSize="small" /> },
    { value: 'in_person', label: 'En Persona', icon: <PersonPin fontSize="small" /> }
  ];

  const contactStatuses = [
    { value: 'no_answer', label: 'Sin Respuesta', color: 'default' },
    { value: 'answered_interested', label: 'Respondió Interesado', color: 'info' },
    { value: 'answered_not_interested', label: 'Respondió Sin Interés', color: 'error' },
    { value: 'requested_callback', label: 'Solicitó Llamada', color: 'warning' },
    { value: 'payment_promised', label: 'Promesa de Pago', color: 'warning' },
    { value: 'payment_completed', label: 'Pago Completado', color: 'success' },
    { value: 'client_unavailable', label: 'Cliente No Disponible', color: 'default' },
    { value: 'wrong_number', label: 'Número Incorrecto', color: 'error' },
    { value: 'hostile_response', label: 'Respuesta Hostil', color: 'error' }
  ];

  const clientBehaviors = [
    { value: 'cooperative', label: 'Cooperativo', color: 'success' },
    { value: 'evasive', label: 'Evasivo', color: 'warning' },
    { value: 'hostile', label: 'Hostil', color: 'error' },
    { value: 'confused', label: 'Confundido', color: 'warning' },
    { value: 'interested', label: 'Interesado', color: 'info' },
    { value: 'indifferent', label: 'Indiferente', color: 'default' }
  ];

  const loadContactLogs = useCallback(async () => {
    if (!renewalId) return;

    try {
      setLoading(true);
      const response = await apiService.getContactHistory(renewalId);
      setContactLogs(response.data.contacts || []);
    } catch (error) {
      console.error('Error loading contact logs:', error);
    } finally {
      setLoading(false);
    }
  }, [renewalId]);

  useEffect(() => {
    if (open && renewalId) {
      loadContactLogs();
    }
  }, [open, renewalId]); // Removed loadContactLogs from dependencies

  const isCandidate = renewalId.startsWith('candidate_');

  const handleSubmit = async () => {
    if (!formData.contactMethod || !formData.contactStatus || !formData.clientBehavior) {
      alert('Por favor complete todos los campos obligatorios');
      return;
    }

    try {
      setSubmitting(true);
      const submitData = new FormData();
      submitData.append('contactMethod', formData.contactMethod);
      submitData.append('contactStatus', formData.contactStatus);
      submitData.append('clientBehavior', formData.clientBehavior);
      submitData.append('conversationNotes', formData.conversationNotes);
      submitData.append('renewalProbability', formData.renewalProbability.toString());

      // Agregar fecha de seguimiento si está presente
      if (formData.followUpDate) {
        submitData.append('followUpDate', formData.followUpDate);
      }

      // Enviar información del dispositivo para candidatos
      if (isCandidate) {
        submitData.append('deviceImei', deviceImei);
        submitData.append('clientName', clientName);
        if (clientEmail) {
          submitData.append('clientEmail', clientEmail);
        }
      }

      await apiService.createContact(renewalId, submitData);

      // Reset form
      setFormData({
        contactMethod: '',
        contactStatus: '',
        clientBehavior: '',
        conversationNotes: '',
        renewalProbability: 50,
        followUpDate: ''
      });

      // Reload contact logs
      await loadContactLogs();

      // Switch to history tab to show the new entry
      setCurrentTab(1);

    } catch (error: any) {
      console.error('Error creating contact log:', error);
      const errorMessage = error.response?.data?.message || 'Error al registrar el contacto. Inténtelo de nuevo.';
      alert(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const formatStatus = (status: string) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatDate = (date: string) => {
    return new Intl.DateTimeFormat('es-DO', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(date));
  };

  const getStatusColor = (status: string) => {
    const statusObj = contactStatuses.find(s => s.value === status);
    return statusObj?.color || 'default';
  };

  const getProbabilityColor = (probability: number) => {
    if (probability >= 75) return 'success';
    if (probability >= 50) return 'info';
    if (probability >= 25) return 'warning';
    return 'error';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: '70vh' } }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h6">Registro de Contacto</Typography>
            <Typography variant="subtitle2" color="text.secondary">
              {clientName} • {deviceImei}
              {clientEmail && (
                <Typography variant="caption" display="block" color="text.secondary">
                  {clientEmail}
                </Typography>
              )}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Tabs
          value={currentTab}
          onChange={(_, value) => setCurrentTab(value)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<Add />} iconPosition="start" label="Registrar Contacto" />
          <Tab icon={<History />} iconPosition="start" label={`Historial (${contactLogs.length})`} />
        </Tabs>

        <TabPanel value={currentTab} index={0}>
          <Box sx={{ p: 3, '& > *': { mb: 2 } }}>
            <Typography variant="h6" gutterBottom>
              Nuevo Intento de Contacto
            </Typography>

            {isCandidate && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Este es un dispositivo candidato para renovación. Se puede registrar el contacto antes de crear la renovación.
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Método de Contacto *</InputLabel>
                <Select
                  value={formData.contactMethod}
                  onChange={(e) => setFormData(prev => ({ ...prev, contactMethod: e.target.value }))}
                  label="Método de Contacto *"
                >
                  {contactMethods.map(method => (
                    <MenuItem key={method.value} value={method.value}>
                      <Box display="flex" alignItems="center" gap={1}>
                        {method.icon}
                        {method.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Resultado del Contacto *</InputLabel>
                <Select
                  value={formData.contactStatus}
                  onChange={(e) => setFormData(prev => ({ ...prev, contactStatus: e.target.value }))}
                  label="Resultado del Contacto *"
                >
                  {contactStatuses.map(status => (
                    <MenuItem key={status.value} value={status.value}>
                      <Chip
                        label={status.label}
                        color={status.color as any}
                        size="small"
                        variant="outlined"
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <FormControl fullWidth>
              <InputLabel>Comportamiento del Cliente *</InputLabel>
              <Select
                value={formData.clientBehavior}
                onChange={(e) => setFormData(prev => ({ ...prev, clientBehavior: e.target.value }))}
                label="Comportamiento del Cliente *"
              >
                {clientBehaviors.map(behavior => (
                  <MenuItem key={behavior.value} value={behavior.value}>
                    <Chip
                      label={behavior.label}
                      color={behavior.color as any}
                      size="small"
                      variant="outlined"
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              key="conversation-notes"
              fullWidth
              multiline
              rows={3}
              label="Notas de la Conversación"
              value={formData.conversationNotes}
              onChange={(e) => setFormData(prev => ({ ...prev, conversationNotes: e.target.value }))}
              placeholder="Describe brevemente la conversación, compromisos, observaciones..."
              inputProps={{ maxLength: 500 }}
            />

            <TextField
              key="follow-up-date"
              fullWidth
              type="date"
              label="Fecha del Próximo Contacto (Opcional)"
              value={formData.followUpDate}
              onChange={(e) => setFormData(prev => ({ ...prev, followUpDate: e.target.value }))}
              InputLabelProps={{
                shrink: true,
              }}
              helperText="Programa una fecha para el próximo contacto si el cliente lo solicita"
            />

            <Box>
              <Typography gutterBottom>
                Probabilidad de Renovación: {formData.renewalProbability}%
              </Typography>
              <Slider
                value={formData.renewalProbability}
                onChange={(_, value) => setFormData(prev => ({ ...prev, renewalProbability: value as number }))}
                min={0}
                max={100}
                step={5}
                color={getProbabilityColor(formData.renewalProbability)}
                marks={[
                  { value: 0, label: '0%' },
                  { value: 25, label: '25%' },
                  { value: 50, label: '50%' },
                  { value: 75, label: '75%' },
                  { value: 100, label: '100%' }
                ]}
              />
            </Box>
          </Box>
        </TabPanel>

        <TabPanel value={currentTab} index={1}>
          <Box sx={{ p: 2 }}>
            {loading ? (
              <Typography textAlign="center" py={4}>Cargando historial...</Typography>
            ) : contactLogs.length === 0 ? (
              <Box textAlign="center" py={4}>
                <History sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No hay contactos registrados
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Registra el primer intento de contacto con este cliente
                </Typography>
              </Box>
            ) : (
              <List>
                {contactLogs.map((log, index) => (
                  <React.Fragment key={log._id}>
                    <ListItem alignItems="flex-start">
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: `${getStatusColor(log.contactStatus)}.main` }}>
                          {contactMethods.find(m => m.value === log.contactMethod)?.icon}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <Chip
                              label={formatStatus(log.contactStatus)}
                              color={getStatusColor(log.contactStatus) as any}
                              size="small"
                            />
                            <Chip
                              label={formatStatus(log.clientBehavior)}
                              size="small"
                              variant="outlined"
                            />
                            <Box display="flex" alignItems="center" gap={0.5} ml="auto">
                              <TrendingUp fontSize="small" color="action" />
                              <Typography variant="body2" fontWeight="bold">
                                {log.renewalProbability}%
                              </Typography>
                            </Box>
                          </Box>
                        }
                        secondary={
                          <>
                            {log.conversationNotes && (
                              <Typography variant="body2" sx={{ mb: 1 }}>
                                {log.conversationNotes}
                              </Typography>
                            )}
                            {log.followUpDate && (
                              <Typography variant="body2" color="warning.main" sx={{ mb: 1, fontWeight: 'medium' }}>
                                📅 Próximo contacto: {new Intl.DateTimeFormat('es-DO', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric'
                                }).format(new Date(log.followUpDate))}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(log.contactDate)} por {log.contactedBy.firstName} {log.contactedBy.lastName}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                    {index < contactLogs.length - 1 && <Divider variant="inset" component="li" />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>
        </TabPanel>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cerrar</Button>
        {currentTab === 0 && (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || !formData.contactMethod || !formData.contactStatus || !formData.clientBehavior}
          >
            {submitting ? 'Registrando...' : 'Registrar Contacto'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ContactLogDialog;